import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { format, FORMATS, parseISO } from "utils/datetime";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import IconButton from "components/IconButton";
import NoPrint from "components/NoPrint";
import SmallAlert from "components/SmallAlert";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";

import MemoSectionContextButton from "entities/Memo/components/Sections/ContextButton";
import NASection from "entities/Memo/components/Sections/NASection";
import SectionHeader from "entities/Memo/components/Sections/SectionHeader";
import { IMemoSection } from "entities/Memo/sdk";
import Table from "entities/TableStorage/components/Table";
import { dealMemoSectionTableUpdate } from "entities/TableStorage/sdk";
import { applyChangesToTableData } from "entities/TableStorage/utils";

interface IChecklistTable {
  dealId: number;
  section: IMemoSection;
  onUpdate?: () => void;
  externalToken?: string;
}

const ChecklistTable = ({
  dealId,
  externalToken,
  section,
  onUpdate,
}: IChecklistTable) => {
  const [saveTimer, setSaveTimer] = useState<NodeJS.Timeout | null>(null);

  const initialTableData = useMemo(
    () =>
      section.checklist_table.map((row) => ({
        cells: row.map(({ value, is_editable: isEditable }) => ({
          value,
          isEditable,
        })),
      })),
    [section.checklist_table]
  );

  const [tableData, setTableData] = useState(initialTableData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmissionDisabled, setIsSubmissionDisabled] = useState(false);
  const [changedCells, setChangedCells] = useState<{
    [key: string]: string | null;
  }>({});

  const handleSaveChanges = useCallback(
    ({
      delta,
      showSuccessMessageAndClose,
    }: {
      showSuccessMessageAndClose: boolean;
      delta: { [key: string]: string | null };
    }) => {
      setIsSubmissionDisabled(true);
      const tableDataWithChangedCells = applyChangesToTableData({
        tableData,
        changedCells: delta,
      });

      const updatedTableData = tableDataWithChangedCells.map(
        (row) => row.cells
      );

      setTableData(
        tableDataWithChangedCells.map((row) => ({
          ...row,
          cells: row.cells.map((cell) => ({
            value: cell.value as string,
            isEditable: _.get(cell, "is_editable", false),
          })),
        }))
      );

      dealMemoSectionTableUpdate({
        dealId,
        memoSectionId: section.section_id,
        rows: updatedTableData,
      })
        .then(() => {
          onUpdate?.();

          if (showSuccessMessageAndClose) {
            setChangedCells({});
            setIsEditing(false);
            toast.successMessage("Table updated successfully!");
          }
        })
        .catch(handleInvalidRequest)
        .finally(() => {
          setIsSubmissionDisabled(false);
        });
    },
    [onUpdate, tableData, setChangedCells, dealId, section.section_id]
  );

  const handleUpdateTableData = useCallback(
    ({ key, currentValue }) => {
      setChangedCells((prev) => {
        const newChangedCells = {
          ...prev,
          [key]: currentValue,
        };

        // Save the data every 3 seconds of inactivity.
        const newTimer = setTimeout(() => {
          handleSaveChanges({
            delta: newChangedCells,
            showSuccessMessageAndClose: false,
          });
        }, 3000);

        setSaveTimer((prevTimer) => {
          if (!_.isNull(prevTimer)) {
            clearTimeout(prevTimer);
          }

          return newTimer;
        });

        return newChangedCells;
      });
    },
    [setChangedCells, handleSaveChanges]
  );

  const handleCancel = useCallback(() => {
    if (!_.isNull(saveTimer)) {
      clearTimeout(saveTimer);
    }

    setIsEditing(false);
  }, [saveTimer]);

  return (
    <>
      <Stack spacing={2}>
        <SectionHeader
          label={section.title}
          alignActionButton="right"
          actionButton={
            _.isUndefined(externalToken) &&
            section && (
              <NoPrint>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton size="small" onClick={() => setIsEditing(true)}>
                    <EditIcon />
                  </IconButton>
                  <MemoSectionContextButton
                    dealId={dealId}
                    section={section}
                    onUpdate={onUpdate}
                  />
                </Stack>
              </NoPrint>
            )
          }
        />
        {section?.is_na ? (
          <NASection />
        ) : (
          <Table
            sx={{
              table: { width: "100%" },
              ...(section.table_section_compact_view
                ? {
                    "tr, td, th, table, tbody": {
                      padding: 0.5,
                      borderTop: 0,
                      borderLeft: 0,
                      borderRight: 0,
                    },
                  }
                : {}),
            }}
            tableData={tableData}
          />
        )}
      </Stack>
      {isEditing && (
        <Dialog
          open
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { padding: theme.spacing(1) } }}
          onClose={handleCancel}
        >
          <DialogTitle
            sx={{
              display: "flex",
              gap: 1,
              margin: theme.spacing(2, 2, 0, 2),
              padding: 2,
              borderRadius: "8px",
              backgroundColor: colors.blue10,
            }}
          >
            <EditIcon />
            <Text variant="h2">{section.title}</Text>
          </DialogTitle>
          <DialogContent
            sx={{
              margin: theme.spacing(2, 2, 0, 2),
              padding: 0,
            }}
          >
            <Table
              editMode
              updateTableData={handleUpdateTableData}
              sx={{
                table: { width: "100%" },
                ...(section.table_section_compact_view
                  ? {
                      "tr, td, th, table, tbody": {
                        padding: 0.5,
                        borderTop: 0,
                        borderLeft: 0,
                        borderRight: 0,
                      },
                    }
                  : {}),
              }}
              tableData={tableData}
            />
          </DialogContent>
          <DialogActions
            sx={{
              padding: 2,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SmallAlert title="Don’t forget to save your changes after editing!" />
              {!_.isNil(section.checklist_table_updated_at) && (
                <Text
                  variant="text3"
                  component="span"
                  sx={{ color: colors.gray80 }}
                >
                  Last saved at{" "}
                  {format(
                    parseISO(section.checklist_table_updated_at),
                    FORMATS.FRONTEND.DATETIME
                  )}
                </Text>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="text" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                disabled={isSubmissionDisabled}
                onClick={() =>
                  handleSaveChanges({
                    delta: changedCells,
                    showSuccessMessageAndClose: true,
                  })
                }
              >
                Save
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
export default ChecklistTable;
